<script setup lang="ts">
import Badge from '../Common/Badge/Badge.vue'
import type { Product } from '~/types/products'
import { useProductCategoriesStore } from '~/stores/product/Categories'
import Stars from '~/components/Product/Stars.vue'
import Rating from '~/components/Product/Rating.vue'
import SkeletonLoader from '~/components/SkeletonLoader.vue'
import ProductStatus from '~/components/Product/ProductStatus.vue'
import Search from '~/components/Product/Preview/Search.vue'
const { $navigate } = useNuxtApp()

interface Props {
	product: Product
	theme: 'full' | 'slim' | 'slim-profile' | 'search'
	showVoting?: boolean
	showDescription?: boolean
	isLoading?: boolean
	emitResult?: boolean
	showTypeTag?: boolean
	closeOnSelect?: boolean
}

const props = defineProps<Props>()
const categoryStore = useProductCategoriesStore()

const slug = await categoryStore().getCategorySlug(props.product.category_id as number)
const emits = defineEmits(['selected', 'close'])

function clicked() {
	if (props.emitResult) {
		emits('selected', props.product)

		if(props.closeOnSelect){
			emits('close')
		}

	} else {

		if(props.closeOnSelect){
			emits('close')
		}

		$navigate(`/products/${slug}/product/${props.product.handle}`)
	}
}

</script>

<template>
	<Search v-if="theme === 'search'" :product="product" :showTypeTag="showTypeTag" @selected="clicked"/>

	<div v-else class="card relative hover:ring-1 hover:ring-green-400 no-underline">

		<NuxtLink
		@click="clicked"
		class="absolute cursor-pointer inset-0" />
		<div
			v-if="theme === 'slim-profile'"
			class="card flex flex-row items-center bg-white hover:bg-gray-50 cursor-pointer">
			<div class="flex p2 py-4 flex-col basis-1/12 border-r border-gray-100 p-2 text-center items-center">
				<SkeletonLoader v-if="isLoading" class="h-8 w-8 rounded-full" />
				<Icon v-else name="ph:shopping-bag-duotone" class="size-7 flex-none text-gray-400" />
			</div>
			<div class="flex flex-col grow basis-8/12 sm:basis-9/12 p-2 sm:px-4">
				<SkeletonLoader v-if="isLoading" class="h-8 w-3/4 mb-2" />
				<h2 v-else class="text-sm sm:text-base font-bold text-green-500 my-0">{{ product.name }}</h2>
				<SkeletonLoader v-if="isLoading" class="h-4 w-1/2" />
				<span v-else class="text-xs text-gray-400">{{ ago(product.created_at) }}</span>
			</div>
			<div class="flex flex-col text-center justify-center items-center basis-1/12 border-l border-gray-100 p-2">
				<SkeletonLoader v-if="isLoading" class="h-8 w-8 rounded-full" />
				<Icon v-else name="ph:star-duotone" class="size-5 flex-none text-gray-500" aria-hidden="true" />
				<SkeletonLoader v-if="isLoading" class="h-4 w-4" />
				<span v-else class="text-sm text-black">{{ product.review_rating }}</span>
			</div>
		</div>

		<div v-else-if="theme === 'full' || theme === 'slim'" class="space-y-4 w-full px-4 py-2 pt-0">
			<div class="flex flex-row gap-2">
				<div class="flex-shrink-0">
					<SkeletonLoader
						v-if="isLoading"
						:class="[theme === 'slim' ? 'mb-4' : 'mb-0', 'w-24 h-24', 'mx-4 mt-4 rounded-lg']" />
					<div v-else>
						<img
							v-if="product.image_url"
							:src="product.image_url"
							:alt="product.name"
							:class="[theme === 'slim' ? 'mb-4' : 'mb-0', 'w-24 h-24', 'mx-4 mt-4 rounded-lg']"
							loading="lazy" />
						<img
							v-else
							src="/images/placeholder-225x225.png"
							:alt="product.name"
							:class="[theme === 'slim' ? 'mb-4' : 'mb-0', 'w-24 h-24', 'mx-4 mt-4 rounded-lg']"
							loading="lazy" />

						<div
							v-if="product.status"
							:class="[theme === 'full' ? 'mt-[-30px]' : 'mt-[-46px] pb-4', 'pl-6 block']"
							role="status">
							<ProductStatus :product="product" />
						</div>
					</div>
				</div>

				<div class="flex-grow flex flex-col">
					<h3 class="text-left text-lg font-semibold text-gray-900 sm:text-xl mb-1 mt-4">
						{{ product.name }}
					</h3>

					<Stars
						:rating="product.review_rating"
						:maxStars="5"
						showRating
						:size="theme === 'full' ? 'md' : 'sm'"
						class="mb-1" />
					<Rating :rating="product.review_rating" :size="theme === 'full' ? 'md' : 'sm'" class="text-left" />
				</div>
			</div>


			<div v-if="theme === 'full' || theme === 'slim'" class="flex flex-row border-t border-gray-100 ">
				<div class="flex items-center gap-4 flex-1">
					<div>
						<Icon
							name="ph:pencil-simple-line-duotone"
							class="size-5 flex-none text-gray-500 icon-fix-md"
							aria-hidden="true" />
						<span class="text-sm text-black ml-1">{{ product.review_count }}</span>
					</div>

					<div>
						<Icon
							name="ph:question-mark-duotone"
							class="size-5 flex-none text-gray-500 icon-fix-md"
							aria-hidden="true" />
						<span class="text-sm text-black ml-1">{{ product.question_count }}</span>
					</div>
					<div>


					<Icon
						name="ph:barcode-duotone"
						class="size-5 flex-none text-gray-500  icon-fix-md"
						aria-hidden="true" />
						<span class="text-sm text-black ml-1">{{ product.coupon_count }}</span>
					</div>
				</div>
				<div class="flex flex-row items-center gap-0 px-0 lg:px-2 pt-2 pb-1 ">
					<NuxtLink
						v-if="product.product_categories?.[0] && theme === 'full'"
						:to="`/products/${product.product_categories[0].handle}`"
						class="text-sm text-green-500 hover:text-green-600 inline-flex items-center relative z-2 hidden lg:block">
						<span class="line-clamp-1 capitalize">{{ product.product_categories[0].name }}</span>
					</NuxtLink>

					<Badge v-if="showTypeTag" color="lime" class="ml-2">PRODUCT</Badge>
				</div>
			</div>
		</div>
	</div>
</template>
